/**
 * A vitrola no painel — o `jukebox_widget.dart` com o `music_card.dart`, o
 * `vinyl_record.dart` e o `listen_along_popup.dart` dentro.
 *
 * Um cartão por pessoa da casa que vinculou o Last.fm, na ordem da casa. Quem
 * não vinculou não aparece: cartão vazio de nome certo é pior do que nada, como
 * diz o comentário de lá.
 *
 * O que o navegador obrigou a mudar:
 *
 * - O `Marquee` do Flutter anda a 28 px por segundo. O CSS não sabe medir
 *   velocidade, só tempo, então o nome é medido depois de desenhado e a duração
 *   sai da conta — é a mesma velocidade, e não uma parecida.
 * - O grão do `_GrainPainter` (180 pontos com semente 42) vira um `feTurbulence`
 *   em SVG. Desenhar 180 círculos num `<canvas>` daria o mesmo cinza por muito
 *   mais trabalho, e este não tem quadro nenhum para animar.
 * - O `ConfettiWidget` dos corações vira quatorze corações com destino próprio
 *   no CSS. O `prefers-reduced-motion` global já os deixa parados.
 * - A linha "Ouviu HH:mm" perde o itálico do `music_card.dart:549`: itálico não
 *   se usa em lugar nenhum desta casa.
 * - Ficaram de fora o balanço de 1,2 px do cartão ao vivo e a pausa de um
 *   segundo entre voltas do letreiro. Os dois custariam um segundo `@keyframes`
 *   por cartão e por largura de texto, e nenhum dos dois se lê.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import {
  hora,
  lerData,
  traduzir,
  usarJukebox,
  usarSessao,
  type EstadoDeMusica,
  type Pessoa,
} from '@nosso-sempre/shared';
import { Icone } from './Icone';
import { OuvirJunto } from './OuvirJunto';
import './jukebox.css';

/** O nome só passeia quando é comprido e está tocando, como lá. */
const NOME_COMPRIDO = 22;

/** `blankSpace: 32.0` e `velocity: 28.0` do `Marquee`. */
const RESPIRO = 32;
const VELOCIDADE = 28;

export function Jukebox() {
  const ouvindo = usarJukebox();
  const { casa } = usarSessao();
  const [aberta, setAberta] = useState<EstadoDeMusica | null>(null);

  const porSlug = new Map(ouvindo.map((o) => [o.slug, o.estado]));
  const cartoes: Array<{ pessoa: Pessoa; estado: EstadoDeMusica }> = (casa?.pessoas ?? []).flatMap(
    (pessoa) => {
      const estado = porSlug.get(pessoa.slug);
      return estado ? [{ pessoa, estado }] : [];
    },
  );

  const noAr = cartoes.some((c) => c.estado.isPlaying);
  // O ovo de páscoa do `_triggerHearts`: quem tocar Ethel Cain ganha corações.
  const carinho = cartoes.some(
    (c) => c.estado.isPlaying && c.estado.artistName.toLowerCase() === 'ethel cain',
  );

  return (
    <div className={noAr ? 'jb no-ar' : 'jb'}>
      <div className="jb-brilho rosa" aria-hidden="true" />
      <div className="jb-brilho lavanda" aria-hidden="true" />
      <div className="jb-grao" aria-hidden="true" />
      <div className="jb-veu" aria-hidden="true" />

      {carinho ? <Coracoes /> : null}

      <div className="jb-corpo">
        <div className="jb-cabeca">
          <span className="jb-selo" aria-hidden="true">
            <Icone nome="onda" tamanho={18} />
          </span>
          <span className="jb-nomes">
            <span className="jb-nome">{traduzir('JUKEBOX')}</span>
            <span className="jb-legenda">
              {noAr ? traduzir('Ao vivo do Last.fm') : traduzir('O que a gente está ouvindo')}
            </span>
          </span>
          <span className="jb-pastilha">
            <span className="jb-ponto" aria-hidden="true" />
            {noAr ? traduzir('NO AR') : traduzir('EM ESPERA')}
          </span>
        </div>

        {cartoes.length === 0 ? (
          <p className="jb-vazio">
            {traduzir('Ninguém vinculou o Last.fm ainda. Isso se faz em Ajustes, em Música.')}
          </p>
        ) : (
          // Em fila lado a lado só cabem duas; de três em diante a coluna é a
          // única leitura que não espreme — a mesma regra do `LayoutBuilder`.
          <div className={cartoes.length > 2 ? 'jb-cartoes empilhados' : 'jb-cartoes'}>
            {cartoes.map(({ pessoa, estado }, i) => (
              <Cartao
                key={pessoa.slug}
                titulo={traduzir('{nome} está ouvindo...', { nome: pessoa.nome })}
                estado={estado}
                ordem={i}
                aoAbrir={() => setAberta(estado)}
              />
            ))}
          </div>
        )}
      </div>

      <OuvirJunto estado={aberta} aoFechar={() => setAberta(null)} />
    </div>
  );
}

/** O `MusicCard`: o vinil que gira atrás da capa, o nome, o artista e o álbum. */
function Cartao({
  titulo,
  estado,
  ordem,
  aoAbrir,
}: {
  titulo: string;
  estado: EstadoDeMusica;
  ordem: number;
  aoAbrir: () => void;
}) {
  const tocando = estado.isPlaying;
  const passeia = tocando && estado.trackName.length > NOME_COMPRIDO;

  return (
    <button
      className={tocando ? 'jb-cartao tocando' : 'jb-cartao'}
      type="button"
      onClick={aoAbrir}
      style={{ ['--jb-ordem' as string]: ordem }}
    >
      {tocando ? <span className="jb-cintila" aria-hidden="true" /> : null}
      {tocando ? <span className="jb-canto" aria-hidden="true" /> : null}

      <span className="jb-cartao-cabeca">
        <span className="jb-cartao-selo">
          <Icone nome="nota-musical" tamanho={11} />
          {titulo}
        </span>
        <span className="jb-cartao-estado">
          <span className="jb-cartao-ponto" aria-hidden="true" />
          {tocando ? 'LIVE' : 'OFFLINE'}
          {tocando ? (
            <span className="jb-barras" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          ) : null}
        </span>
      </span>

      <span className="jb-cartao-linha">
        <span className="jb-arte">
          {tocando ? <Vinil /> : null}
          <span className="jb-capa">
            {estado.imageUrl ? (
              <img src={estado.imageUrl} alt="" width={84} height={84} loading="lazy" />
            ) : (
              <span className="jb-sem-capa" aria-hidden="true">
                <Icone nome="nota-musical" tamanho={32} />
              </span>
            )}
            <span className="jb-vidro" aria-hidden="true" />
            {tocando ? (
              <span className="jb-tocar" aria-hidden="true">
                <Icone nome="tocar" tamanho={14} preenchido />
              </span>
            ) : null}
          </span>
        </span>

        <span className="jb-textos">
          {passeia ? (
            <Passeio texto={estado.trackName} />
          ) : (
            <span className="jb-faixa">{estado.trackName || '—'}</span>
          )}
          <span className="jb-artista">{estado.artistName || traduzir('Unknown Artist')}</span>
          {estado.albumName ? <span className="jb-album">{estado.albumName}</span> : null}
          {tocando ? (
            <span className="jb-convite">
              <span className="jb-convite-ponto" aria-hidden="true" />
              {traduzir('Toque para ouvir junto →')}
            </span>
          ) : (
            <span className="jb-quando">
              <Icone nome="relogio" tamanho={12} />
              {rotuloDoRelogio(estado)}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

/** `'Ouviu HH:mm'` quando há hora, `'Sem clima agora'` quando não há. */
function rotuloDoRelogio(estado: EstadoDeMusica): string {
  const quando = lerData(estado.timestamp);
  return quando ? `Ouviu ${hora(quando)}` : traduzir('Sem clima agora');
}

/**
 * O `Marquee`: o nome anda para a esquerda até a segunda cópia tomar o lugar da
 * primeira, e recomeça sem emenda.
 *
 * A duração é medida e não escrita à mão porque o `velocity: 28.0` de lá é
 * pixels por segundo — um tempo fixo faria um nome curto correr e um comprido
 * arrastar.
 */
function Passeio({ texto }: { texto: string }) {
  const tira = useRef<HTMLSpanElement>(null);
  const [segundos, setSegundos] = useState(0);

  useLayoutEffect(() => {
    const largura = tira.current?.offsetWidth ?? 0;
    setSegundos((largura + RESPIRO) / VELOCIDADE);
  }, [texto]);

  return (
    <span className="jb-passeio">
      <span className="jb-fita" style={{ ['--jb-passo' as string]: `${segundos.toFixed(2)}s` }}>
        <span ref={tira}>{texto}</span>
        <span aria-hidden="true">{texto}</span>
      </span>
    </span>
  );
}

/** O `VinylRecord`: 62 px de disco girando em três segundos por volta. */
function Vinil() {
  return (
    <span className="jb-vinil" aria-hidden="true">
      <span className="jb-sulcos" />
      <span className="jb-lustro" />
      <span className="jb-selo-vinil">
        <span className="jb-furo" />
      </span>
    </span>
  );
}

/** Os quatorze corações do `ConfettiWidget`, cada um com o seu destino. */
function Coracoes() {
  return (
    <span className="jb-coracoes" aria-hidden="true">
      {Array.from({ length: 14 }, (_, i) => {
        const angulo = (i / 14) * 2 * Math.PI;
        return (
          <i
            key={i}
            style={{
              ['--jb-x' as string]: `${Math.cos(angulo) * 140}px`,
              ['--jb-y' as string]: `${Math.sin(angulo) * 110}px`,
              ['--jb-atraso' as string]: `${i * 60}ms`,
            }}
          >
            <Icone nome="coracao" tamanho={14} preenchido />
          </i>
        );
      })}
    </span>
  );
}
